import { useState, useMemo, useEffect, useRef } from 'react'
import { Search, Clock, CheckCircle, Play, X, Eye, EyeOff, Calendar, ChevronDown } from 'lucide-react'
import { trackScheduleActivity } from '../config/analytics'

function ScheduleView({ categories, tournament, themeClasses, isDarkMode, colorTheme }) {
  // Collect all matches across categories with category metadata
  const allMatches = useMemo(() => {
    const matches = []
    categories.forEach(category => {
      category.matches.forEach(match => {
        if (match.scheduledTime) {
          matches.push({
            ...match,
            categoryTitle: category.title,
            categorySkillLevel: category.skillLevel,
            categoryAgeGroup: category.ageGroup,
            categoryId: category.id,
            matchDuration: category.matchDuration || 15
          })
        }
      })
    })
    return matches
  }, [categories])

  // Derive unique days from matches
  const days = useMemo(() => {
    const daySet = new Map()
    allMatches.forEach(m => {
      const date = new Date(m.scheduledTime)
      const key = date.toDateString()
      if (!daySet.has(key)) {
        daySet.set(key, date)
      }
    })
    return Array.from(daySet.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([key, date]) => ({
        key,
        date,
        label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      }))
  }, [allMatches])

  // Default to tournament activeDay if set, then today if it's a tournament day, otherwise first day
  const [selectedDay, setSelectedDay] = useState(() => {
    // Prefer the tournament's activeDay
    if (tournament?.activeDay) {
      const ad = tournament.activeDay.toDate ? tournament.activeDay.toDate() : new Date(tournament.activeDay)
      const adKey = ad.toDateString()
      const activeDay = days.find(d => d.key === adKey)
      if (activeDay) return activeDay.key
    }
    const today = new Date().toDateString()
    const todayDay = days.find(d => d.key === today)
    return todayDay ? todayDay.key : days[0]?.key || ''
  })

  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [hideNonMatching, setHideNonMatching] = useState(false)
  const searchDebounceRef = useRef(null)

  // Track schedule viewed on mount
  useEffect(() => {
    trackScheduleActivity('Viewed', { label: selectedDay, value: allMatches.length, category_count: categories.length })
  }, [])

  // Helper to get player name string
  const getPlayerName = (player) => {
    if (!player) return 'TBD'
    if (typeof player === 'string') return player
    if (typeof player === 'object') {
      const name = [player.firstName, player.lastName].filter(Boolean).join(' ').trim()
      return name || 'TBD'
    }
    return 'TBD'
  }

  // Check if a match is a placeholder (no teams assigned)
  const isPlaceholder = (match) => !match.team1Id || !match.team2Id

  // Check if a match matches the search query
  const matchesSearch = (match, query) => {
    if (!query.trim()) return true
    const q = query.toLowerCase()
    const names = [
      getPlayerName(match.team1Player1),
      getPlayerName(match.team1Player2),
      getPlayerName(match.team2Player1),
      getPlayerName(match.team2Player2),
      match.team1Name,
      match.team2Name
    ].filter(Boolean).map(n => n.toLowerCase())
    return names.some(n => n.includes(q))
  }

  // Court label helper
  const getCourtLabel = (courtNumber) => {
    if (tournament.courts && tournament.courts[courtNumber - 1]) {
      return tournament.courts[courtNumber - 1]
    }
    return `Court ${courtNumber}`
  }

  // Courts array
  const courts = useMemo(() => {
    const courtNums = new Set()
    allMatches.forEach(m => {
      if (m.courtNumber) courtNums.add(m.courtNumber)
    })
    // Fall back to tournament.numberOfCourts if no court data
    if (courtNums.size === 0) {
      for (let i = 1; i <= (tournament.numberOfCourts || 4); i++) {
        courtNums.add(i)
      }
    }
    return Array.from(courtNums).sort((a, b) => a - b)
  }, [allMatches, tournament.numberOfCourts])

  // Unique categories for filter — only those with matches on the selected day, sorted by earliest match time
  const categoryOptions = useMemo(() => {
    // Find which category IDs have matches on the selected day, and their earliest time
    const categoryEarliestTime = new Map()
    allMatches.forEach(m => {
      if (new Date(m.scheduledTime).toDateString() === selectedDay) {
        const time = new Date(m.scheduledTime).getTime()
        if (!categoryEarliestTime.has(m.categoryId) || time < categoryEarliestTime.get(m.categoryId)) {
          categoryEarliestTime.set(m.categoryId, time)
        }
      }
    })
    return categories
      .filter(c => categoryEarliestTime.has(c.id))
      .sort((a, b) => (categoryEarliestTime.get(a.id) || 0) - (categoryEarliestTime.get(b.id) || 0))
      .map(c => ({
        id: c.id,
        label: [c.title, c.skillLevel, c.ageGroup].filter(Boolean).join(' • ')
      }))
  }, [categories, allMatches, selectedDay])

  // Filter and group matches
  const { timeSlots, filteredMatches } = useMemo(() => {
    // Filter by day
    let matches = allMatches.filter(m => {
      const matchDay = new Date(m.scheduledTime).toDateString()
      return matchDay === selectedDay
    })

    // Filter by category
    if (selectedCategory !== 'all') {
      matches = matches.filter(m => m.categoryId === selectedCategory)
    }

    // Group by time slot
    const slotMap = new Map()
    matches.forEach(m => {
      const time = m.scheduledTime
      if (!slotMap.has(time)) {
        slotMap.set(time, [])
      }
      slotMap.get(time).push(m)
    })

    const timeSlots = Array.from(slotMap.entries())
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([time, slotMatches]) => ({
        time,
        label: new Date(time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
        matches: slotMatches.sort((a, b) => a.courtNumber - b.courtNumber)
      }))

    return { timeSlots, filteredMatches: matches }
  }, [allMatches, selectedDay, selectedCategory])

  const hasSearch = searchQuery.trim().length > 0

  // Status badge component
  const StatusBadge = ({ status }) => {
    if (status === 'completed') {
      return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
          isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
        }`}>
          <CheckCircle className="w-2.5 h-2.5" />
          Done
        </span>
      )
    }
    if (status === 'in_progress') {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"></span>
          Live
        </span>
      )
    }
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
        isDarkMode ? 'bg-white/[0.06] text-white/40' : 'bg-gray-100 text-gray-500'
      }`}>
        <Clock className="w-2.5 h-2.5" />
      </span>
    )
  }

  // Category badge
  const CategoryBadge = ({ match }) => (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
      isDarkMode ? 'bg-white/[0.06] text-white/50' : 'bg-gray-100 text-gray-600'
    } truncate max-w-full`}>
      {[match.categoryTitle, match.categorySkillLevel].filter(Boolean).join(' • ')}
    </span>
  )

  // Match card for mobile timeline
  const MatchCard = ({ match }) => {
    const placeholder = isPlaceholder(match)
    const searched = hasSearch && matchesSearch(match, searchQuery)
    const dimmed = hasSearch && !searched

    return (
      <div className={`rounded-xl border p-3 transition-all duration-300 ${
        placeholder
          ? (isDarkMode ? 'bg-white/[0.02] border-white/[0.04] opacity-40' : 'bg-gray-50 border-gray-200 opacity-40')
          : dimmed
            ? (isDarkMode ? 'bg-white/[0.03] border-white/[0.06] opacity-30' : 'bg-white border-gray-200 opacity-30')
            : (isDarkMode ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white border-gray-200 shadow-sm')
      }`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              isDarkMode ? 'bg-white/[0.08] text-white/60' : 'bg-gray-200 text-gray-600'
            }`}>
              {getCourtLabel(match.courtNumber)}
            </span>
            <CategoryBadge match={match} />
          </div>
          <StatusBadge status={match.status} />
        </div>

        {/* Teams */}
        <div className="space-y-1.5">
          <div className={`flex items-center justify-between ${
            !placeholder && match.winnerId === match.team1Id ? `font-bold ${colorTheme.text.primary}` : themeClasses.text
          }`}>
            <span className="text-sm font-semibold truncate flex-1">
              {placeholder ? 'TBD' : `${getPlayerName(match.team1Player1)} / ${getPlayerName(match.team1Player2)}`}
            </span>
            {match.status !== 'scheduled' && match.status !== 'pending' && (
              <span className="text-sm font-black ml-2">{match.team1Score || 0}</span>
            )}
          </div>
          <div className={`text-[10px] font-bold uppercase tracking-wider ${themeClasses.textMuted}`}>vs</div>
          <div className={`flex items-center justify-between ${
            !placeholder && match.winnerId === match.team2Id ? `font-bold ${colorTheme.text.primary}` : themeClasses.text
          }`}>
            <span className="text-sm font-semibold truncate flex-1">
              {placeholder ? 'TBD' : `${getPlayerName(match.team2Player1)} / ${getPlayerName(match.team2Player2)}`}
            </span>
            {match.status !== 'scheduled' && match.status !== 'pending' && (
              <span className="text-sm font-black ml-2">{match.team2Score || 0}</span>
            )}
          </div>
        </div>

        {/* Stage */}
        {match.stageName && (
          <div className={`mt-2 text-[10px] ${themeClasses.textMuted}`}>
            {match.stageName}
          </div>
        )}
      </div>
    )
  }

  // Desktop grid cell
  const GridCell = ({ match }) => {
    if (!match) {
      return <div className="h-[120px]"></div>
    }

    const placeholder = isPlaceholder(match)
    const searched = hasSearch && matchesSearch(match, searchQuery)
    const dimmed = hasSearch && !searched

    return (
      <div className={`h-[120px] border rounded-lg p-2.5 transition-all duration-300 overflow-hidden ${
        placeholder
          ? (isDarkMode ? 'bg-white/[0.02] border-white/[0.04] opacity-40' : 'bg-gray-50 border-gray-200 opacity-40')
          : dimmed
            ? (isDarkMode ? 'bg-white/[0.03] border-white/[0.06] opacity-30' : 'bg-white border-gray-200 opacity-30')
            : (isDarkMode ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white border-gray-200 shadow-sm')
      }`}>
        <div className="flex items-center justify-between mb-1.5">
          <CategoryBadge match={match} />
          <StatusBadge status={match.status} />
        </div>
        <div className={`text-xs font-semibold leading-tight ${
          !placeholder && match.winnerId === match.team1Id ? colorTheme.text.primary : themeClasses.text
        }`}>
          {placeholder ? 'TBD' : `${getPlayerName(match.team1Player1)} / ${getPlayerName(match.team1Player2)}`}
        </div>
        <div className={`text-[10px] font-bold my-0.5 ${themeClasses.textMuted}`}>vs</div>
        <div className={`text-xs font-semibold leading-tight ${
          !placeholder && match.winnerId === match.team2Id ? colorTheme.text.primary : themeClasses.text
        }`}>
          {placeholder ? 'TBD' : `${getPlayerName(match.team2Player1)} / ${getPlayerName(match.team2Player2)}`}
        </div>
        {match.stageName && (
          <div className={`mt-1 text-[10px] ${themeClasses.textMuted} truncate`}>
            {match.stageName}
          </div>
        )}
      </div>
    )
  }

  if (days.length === 0) {
    return (
      <div className="animate-fade-in text-center py-12">
        <Clock className={`w-16 h-16 mx-auto mb-4 ${themeClasses.textMuted}`} />
        <p className={`font-semibold ${themeClasses.textMuted}`}>No scheduled matches yet</p>
      </div>
    )
  }

  return (
    <div className="animate-fade-in space-y-5">
      <h2 className={`text-2xl sm:text-3xl font-black tracking-tight ${themeClasses.text}`}>Schedule</h2>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        {/* Day Filter Tabs */}
        {days.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {days.map(day => {
              const isActiveDay = day.key === selectedDay
              return (
                <button
                  key={day.key}
                  onClick={() => {
                    setSelectedDay(day.key)
                    setSelectedCategory('all')
                    trackScheduleActivity('Day Selected', { label: day.label })
                  }}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                    isActiveDay
                      ? isDarkMode
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                      : isDarkMode
                        ? 'bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {day.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Category + Search Row */}
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Category Selector */}
          <div className="relative w-full sm:w-auto">
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value)
                const catLabel = e.target.value === 'all' ? 'All Categories' : categoryOptions.find(c => c.id === e.target.value)?.label || e.target.value
                trackScheduleActivity('Category Filtered', { label: catLabel })
              }}
              className={`appearance-none w-full sm:w-auto pl-4 pr-10 py-2.5 rounded-xl font-semibold text-sm border transition-all duration-300 cursor-pointer ${
                isDarkMode
                  ? 'bg-white/[0.06] border-white/[0.12] text-white hover:bg-white/[0.1]'
                  : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
              } focus:outline-none focus:ring-2 focus:ring-emerald-500/50`}
            >
              <option value="all">All Categories</option>
              {categoryOptions.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${themeClasses.textMuted}`} />
          </div>

          {/* Player Search */}
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${themeClasses.textMuted}`} />
            <input
              type="text"
              placeholder="Search player name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                // Debounce search tracking to avoid excessive events
                if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
                if (e.target.value.trim().length >= 2) {
                  searchDebounceRef.current = setTimeout(() => {
                    const matchCount = allMatches.filter(m => matchesSearch(m, e.target.value)).length
                    trackScheduleActivity('Player Search', { label: e.target.value.trim(), value: matchCount })
                  }, 800)
                }
              }}
              className={`w-full pl-9 pr-9 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-300 ${
                isDarkMode
                  ? 'bg-white/[0.06] border-white/[0.12] text-white placeholder-gray-500 hover:bg-white/[0.1]'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 hover:bg-gray-50'
              } focus:outline-none focus:ring-2 focus:ring-emerald-500/50`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textMuted}`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Hide non-matching toggle */}
          {hasSearch && (
            <button
              onClick={() => {
                setHideNonMatching(!hideNonMatching)
                trackScheduleActivity('Filter Toggle', { label: !hideNonMatching ? 'on' : 'off' })
              }}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                hideNonMatching
                  ? isDarkMode
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                  : isDarkMode
                    ? 'bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
              }`}
              title={hideNonMatching ? 'Show all matches' : 'Show only matching'}
            >
              {hideNonMatching ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span className="hidden sm:inline">{hideNonMatching ? 'Filtered' : 'Show only'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Match count */}
      <div className={`text-xs font-semibold ${themeClasses.textMuted}`}>
        {filteredMatches.length} match{filteredMatches.length !== 1 ? 'es' : ''}
        {hasSearch && ` • ${filteredMatches.filter(m => matchesSearch(m, searchQuery)).length} matching "${searchQuery}"`}
      </div>

      {/* Mobile Timeline View */}
      <div className="md:hidden space-y-4">
        {timeSlots.length === 0 ? (
          <div className="text-center py-8">
            <p className={`font-semibold ${themeClasses.textMuted}`}>No matches for this day</p>
          </div>
        ) : (
          timeSlots.map(slot => {
            const visibleMatches = slot.matches
              .filter(match => !(hasSearch && hideNonMatching && !matchesSearch(match, searchQuery)))
            if (visibleMatches.length === 0) return null
            return (
              <div key={slot.time}>
                {/* Time header */}
                <div className="flex items-center gap-3 mb-2">
                  <div className={`text-sm font-black ${colorTheme.text.primary}`}>
                    {slot.label}
                  </div>
                  <div className={`flex-1 h-px ${isDarkMode ? 'bg-white/[0.08]' : 'bg-gray-200'}`}></div>
                  <span className={`text-[10px] font-semibold ${themeClasses.textMuted}`}>
                    {visibleMatches.length} match{visibleMatches.length !== 1 ? 'es' : ''}
                  </span>
                </div>
                {/* Match cards */}
                <div className="space-y-2">
                  {visibleMatches.map(match => (
                    <MatchCard key={match.id} match={match} />
                  ))}
                </div>
              </div>
            )
          }))
        }
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={`sticky left-0 z-10 px-3 py-3 text-left text-xs font-bold uppercase tracking-wider ${
                isDarkMode ? 'bg-[#0a0a0a] text-white/40' : 'bg-[#fafafa] text-gray-500'
              }`}>
                Time
              </th>
              {courts.map(court => (
                <th key={court} className={`px-2 py-3 text-center text-xs font-bold uppercase tracking-wider min-w-[180px] ${
                  isDarkMode ? 'text-white/60' : 'text-gray-600'
                }`}>
                  <span className={`inline-block px-3 py-1 rounded-full bg-gradient-to-r ${colorTheme.gradient} text-white text-[10px]`}>
                    {getCourtLabel(court)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.length === 0 ? (
              <tr>
                <td colSpan={courts.length + 1} className="text-center py-8">
                  <p className={`font-semibold ${themeClasses.textMuted}`}>No matches for this day</p>
                </td>
              </tr>
            ) : (
              timeSlots.map(slot => (
                <tr key={slot.time} className={`border-t ${isDarkMode ? 'border-white/[0.06]' : 'border-gray-100'}`}>
                  <td className={`sticky left-0 z-10 px-3 py-2 align-top ${
                    isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#fafafa]'
                  }`}>
                    <span className={`text-sm font-black ${colorTheme.text.primary}`}>{slot.label}</span>
                  </td>
                  {courts.map(court => {
                    const match = slot.matches.find(m => m.courtNumber === court)
                    const hidden = match && hasSearch && hideNonMatching && !matchesSearch(match, searchQuery)
                    return (
                      <td key={court} className="px-1.5 py-1.5 align-top">
                        <GridCell match={hidden ? null : match} />
                      </td>
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ScheduleView
